<template>
  <div
    id="advanced_settings_tab"
    class="flex flex-col gap-6 proxima_nova max-w-4xl mx-auto w-full h-full overflow-y-auto px-2"
  >
    <VoiceAgentSectionHeader
      :title="tabHeaders.advancedSettings.title"
      :description="tabHeaders.advancedSettings.description"
    />

    <div class="flex flex-col gap-10">
      <div class="form_col gap-2">
        <span class="secondary_color text-[15px]">Background Sound</span>
        <a-select
          :value="formData.background_sound"
          @change="(value) => updateFormField('background_sound', value)"
          :options="backgroundSoundOptions"
          class="w-full"
          placeholder="Select background sound"
        >
          <template #suffixIcon>
            <ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="bottom_arrow"
              class="size-4"
            />
          </template>
        </a-select>
      </div>

      <div class="flex items-center gap-4">
        <a-switch
          size="small"
          :checked="formData.smart_noise_cancellation"
          @change="
            (checked) => updateFormField('smart_noise_cancellation', checked)
          "
        />
        <span class="font-semibold"
          >Enable Smart Active Noise Cancellation</span
        >
      </div>

      <CustomSlider
        v-bind="responsivenessSlider"
        @update:value="(value) => updateFormField('responsiveness', value)"
      />

      <CustomSlider
        v-bind="interruptionSlider"
        @update:value="
          (value) => updateFormField('interruption_sensitivity', value)
        "
      />

      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <h3 class="text-base font-semibold">Agent Follow-up</h3>
          <p class="text-sm secondary_color">
            {{ advancedSettingsConfig.agentFollowUp.description }}
          </p>
        </div>

        <div class="flex items-center gap-2">
          <span class="text-sm">Agent will follow up on silence</span>
          <a-input-number
            :value="formData.follow_up_times"
            :min="1"
            :max="100"
            class="w-12"
            @change="(value) => updateFormField('follow_up_times', value)"
          />
          <span class="text-[13px]">time(s) at</span>
          <a-input-number
            :value="formData.follow_up_interval"
            :min="1"
            :max="60"
            class="w-12"
            @change="(value) => updateFormField('follow_up_interval', value)"
          />
          <span class="text-[13px]">seconds interval</span>
        </div>

        <div class="form_col gap-2">
          <span class="secondary_color text-[13px]">Agent Timezone</span>
          <a-select
            :value="formData.agent_timezone"
            @change="(value) => updateFormField('agent_timezone', value)"
            :options="allTimezones"
            class="w-1/2"
            placeholder="Select timezone"
            show-search
          >
            <template #suffixIcon>
              <ThemeImage
                :icon="imageNames.BOTTOM_ARROW"
                alt="bottom_arrow"
                class="size-4"
              />
            </template>
          </a-select>
        </div>
      </div>

      <CustomSlider
        v-bind="maximumCallDurationSlider"
        @update:value="
          (value) => updateFormField('maximum_call_duration', value)
        "
      />

      <CustomSlider
        v-bind="endCallOnSilenceSlider"
        @update:value="(value) => updateFormField('end_call_on_silence', value)"
      />

      <CustomSlider
        v-bind="pauseBeforeSpeakingSlider"
        @update:value="
          (value) => updateFormField('pause_before_speaking', value)
        "
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { message } from "ant-design-vue";
import { useRoute } from "vue-router";
import { isEqual, debounce } from "lodash";
import ThemeImage from "@/src/components/ThemeImage.vue";
import VoiceAgentSectionHeader from "@/src/components/voice_agents/VoiceAgentSectionHeader.vue";
import CustomSlider from "@/src/components/custom_components/CustomSlider.vue";
import VoiceAgentService from "@/src/api/VoiceAgentService.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import {
  tabHeaders,
  backgroundSoundOptions,
  advancedSettingsConfig,
} from "@/src/constants/voiceAgentConstants.js";
import { getFormattedTimezones } from "@/src/utils/timezoneUtils.js";
import { convertAdvancedSettingsToApiPayload } from "@/src/utils/voiceAgentUtils.js";

const route = useRoute();

const props = defineProps({
  formData: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["update:formData"]);

const isCreating = ref(false);
const isPublishing = ref(false);
const advancedSettingsFormEdited = ref(false);
const originalFormData = ref(null);

const agentId = computed(() => route.params?.id);
const isEditMode = computed(() => !!agentId.value);

const responsivenessSlider = computed(() => ({
  value:
    props.formData.responsiveness ||
    advancedSettingsConfig.responsiveness.default,
  ...advancedSettingsConfig.responsiveness,
}));

const interruptionSlider = computed(() => ({
  value:
    props.formData.interruption_sensitivity ||
    advancedSettingsConfig.interruptionSensitivity.default,
  ...advancedSettingsConfig.interruptionSensitivity,
}));

const maximumCallDurationSlider = computed(() => ({
  value:
    props.formData.maximum_call_duration ||
    advancedSettingsConfig.maximumCallDuration.default,
  ...advancedSettingsConfig.maximumCallDuration,
}));

const endCallOnSilenceSlider = computed(() => ({
  value:
    props.formData.end_call_on_silence ||
    advancedSettingsConfig.endCallOnSilence.default,
  ...advancedSettingsConfig.endCallOnSilence,
}));

const pauseBeforeSpeakingSlider = computed(() => ({
  value:
    props.formData.pause_before_speaking ||
    advancedSettingsConfig.pauseBeforeSpeaking.default,
  ...advancedSettingsConfig.pauseBeforeSpeaking,
}));

const allTimezones = ref(getFormattedTimezones());

const updateFormField = (field, value) => {
  try {
    emit("update:formData", {
      ...props.formData,
      [field]: value,
    });
  } catch (error) {
    console.error(`Error updating ${field}:`, error);
  }
};

const handleFormDataChange = () => {
  try {
    if (originalFormData.value) {
      advancedSettingsFormEdited.value = !isEqual(
        props.formData,
        originalFormData.value,
      );

      if (isEditMode.value && advancedSettingsFormEdited.value) {
        debouncedPublish();
      }
    }
  } catch (error) {
    console.error("Error in formData watcher:", error);
  }
};

const handlePublish = async () => {
  try {
    if (!advancedSettingsFormEdited.value) {
      return;
    }
    if (!props.formData?.agent_id) {
      message.error("Agent ID is missing");
      return;
    }

    isPublishing.value = true;

    const payload = convertAdvancedSettingsToApiPayload(props.formData);
    const response = await VoiceAgentService.createAdvancedSettings(
      props.formData.agent_id,
      payload,
    );

    if (response?.success) {
      advancedSettingsFormEdited.value = false;
      originalFormData.value = { ...props.formData };
    }
  } catch (error) {
    console.error("Error publishing advanced settings:", error);
    message.error("Failed to publish advanced settings");
  } finally {
    isPublishing.value = false;
  }
};

const debouncedPublish = debounce(async () => {
  await handlePublish();
}, 3000);

const handleDiscard = () => {
  try {
    if (originalFormData.value) {
      emit("update:formData", { ...originalFormData.value });
      advancedSettingsFormEdited.value = false;
    }
  } catch (error) {
    console.error("Error discarding changes:", error);
  }
};

const handleContinue = async () => {
  try {
    if (!props.formData?.agent_id) {
      message.error("Agent ID is missing");
      return false;
    }
    isCreating.value = true;

    const payload = convertAdvancedSettingsToApiPayload(props.formData);

    const response = await VoiceAgentService.createAdvancedSettings(
      props.formData.agent_id,
      payload,
    );

    if (response?.success) {
      message.success("Agent Created successfully!");
      return true;
    }
    message.error("Failed to save advanced settings");
    return false;
  } catch (error) {
    console.error("Error saving advanced settings:", error);
    message.error("Failed to save advanced settings");
    return false;
  } finally {
    isCreating.value = false;
  }
};

watch(() => props.formData, handleFormDataChange, { deep: true });

defineExpose({
  handleContinue,
  isCreating,
});

onMounted(() => {
  if (isEditMode.value && props.formData) {
    originalFormData.value = { ...props.formData };
  }
});

onBeforeUnmount(async () => {
  debouncedPublish.cancel();
  await handlePublish();
});
</script>
